import React, { useEffect, useState } from 'react';
import { Button, Flag, Heading, Message, Tooltip } from '@lumx/react';
import { mdiPlus, mdiThumbUpOutline, mdiThumbDownOutline } from '@mdi/js';
import MdiIcon from '@mdi/react';
import CreateTestPage from './CreateTestPage';
import { TestRun, Tester } from '../types';
import { useWizard } from '../context/WizardContext';

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_TESTS: TestRun[] = [
  {
    id: 't1',
    name: 'Americas dry run',
    scopeCount: 2,
    scopeUnits: ['Americas', 'US'],
    testers: [
      { initials: 'JD', color: '#5c6bc0', name: 'Jamie Dupont' },
      { initials: 'ML', color: '#26a69a', name: 'Marie Lambert' },
      { initials: 'RP', color: '#ef5350', name: 'Rémi Poirier' },
    ],
    thumbsUp: 8,
    thumbsDown: 1,
    startDate: 'Mar 12, 2026',
    status: 'complete',
  },
  {
    id: 't2',
    name: 'EU pilot',
    scopeCount: 3,
    scopeUnits: ['Europe', 'France', 'United Kingdom'],
    testers: [
      { initials: 'SC', color: '#ab47bc', name: 'Sophie Chen' },
      { initials: 'TH', color: '#42a5f5', name: 'Thomas Herrera' },
      { initials: 'AV', color: '#66bb6a', name: 'Alex Vidal' },
      { initials: 'BK', color: '#ffa726', name: 'Björn Keller' },
      { initials: 'NL', color: '#26c6da', name: 'Nadia Lemaire' },
      { initials: 'MR', color: '#ec407a', name: 'Marco Rossi' },
    ],
    thumbsUp: 4,
    thumbsDown: 2,
    startDate: 'Apr 3, 2026',
    status: 'running',
    daysLeft: 3,
  },
  {
    id: 't3',
    name: 'APAC rollout test',
    scopeCount: 3,
    scopeUnits: ['APAC', 'Japan', 'Singapore'],
    testers: [
      { initials: 'KL', color: '#8d6e63', name: 'Keiko Lawson' },
      { initials: 'YT', color: '#78909c', name: 'Yuki Tanaka' },
    ],
    thumbsUp: 0,
    thumbsDown: 0,
    startDate: '—',
    status: 'draft',
  },
];

// ── AvatarStack ───────────────────────────────────────────────────────────────

const AvatarStack: React.FC<{ testers: Tester[] }> = ({ testers }) => {
  const visible = testers.slice(0, 5);
  const overflow = testers.length - 5;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visible.map((t, i) => (
        <Tooltip key={i} label={t.name} placement="top">
          <div
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: t.color, color: 'white',
              fontSize: 10, fontWeight: 600, letterSpacing: '0.03em',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid white',
              marginLeft: i === 0 ? 0 : -6,
              position: 'relative', zIndex: visible.length - i,
              flexShrink: 0,
              cursor: 'default',
            }}
          >
            {t.initials}
          </div>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <div
          style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(0,0,0,0.10)', color: 'rgba(0,0,0,0.60)',
            fontSize: 10, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid white',
            marginLeft: -6, position: 'relative',
            flexShrink: 0,
          }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};

// ── StatusFlag ────────────────────────────────────────────────────────────────

const StatusFlag: React.FC<{ test: TestRun }> = ({ test }) => {
  switch (test.status) {
    case 'complete': return <Flag color="green" label="Complete" />;
    case 'running':  return <Flag color="blue" label={`${test.daysLeft} days left`} />;
    case 'draft':    return <Flag color="grey" label="Draft" />;
  }
};

// ── Table layout ──────────────────────────────────────────────────────────────

// Name capped at 220px so Scope sits close to it; Scope gets 1fr for unit name room
// Status fixed at 130px (widest flag "3 DAYS LEFT" ≈ 110px); justifyItems:start makes flags shrink to content
const COLS = 'minmax(160px, 220px) 1fr 160px 120px 140px 130px';
const HEADERS = ['Name', 'Scope', 'Testers', 'Feedback', 'Start date', 'Status'];

// ── TestDeploymentPage ────────────────────────────────────────────────────────

const TestDeploymentPage: React.FC = () => {
  const { updateTestStats } = useWizard();
  const [creating, setCreating] = useState(false);
  const [tests, setTests] = useState<TestRun[]>(MOCK_TESTS);

  useEffect(() => {
    updateTestStats(
      tests.filter(t => t.status === 'running').length,
      tests.filter(t => t.status === 'complete').length,
    );
  }, [tests, updateTestStats]);

  const handleLaunch = (run: TestRun) => {
    setTests(prev => [run, ...prev]);
    setCreating(false);
  };

  if (creating) {
    return <CreateTestPage onBack={() => setCreating(false)} onLaunch={handleLaunch} onSaveDraft={handleLaunch} />;
  }

  return (
    <div className="main-content" style={{ overflow: 'auto' }}>
      <div className="page-header">
        <div className="page-header-left">
          <Heading as="h1" typography="headline">Test deployment</Heading>
        </div>
        <div className="page-header-right">
          <Button emphasis="high" color="primary" leftIcon={mdiPlus} onClick={() => setCreating(true)}>
            Create test
          </Button>
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <Message kind="info" hasBackground>
          Tests are sandboxed previews. Your live sites are not affected — admins can run as many rounds as needed before scheduling the final Deployment.
        </Message>
      </div>

      <div style={{ marginTop: 24 }}>
        {/* Column headers */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            justifyItems: 'start',
            padding: '0 24px',
            height: 51,
            alignItems: 'center',
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            background: 'white',
          }}
        >
          {HEADERS.map(h => (
            <span key={h} style={{ fontSize: 12, color: 'rgba(0,0,0,0.60)', fontWeight: 400 }}>
              {h}
            </span>
          ))}
        </div>

        {/* Data rows */}
        {tests.map((test, idx) => (
          <div
            key={test.id}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              justifyItems: 'start',
              padding: '0 24px',
              height: 60,
              alignItems: 'center',
              borderBottom: idx < tests.length - 1 ? '1px solid rgba(0,0,0,0.08)' : undefined,
            }}
          >
            {/* Name */}
            <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.87)', paddingRight: 16, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {test.name}
            </span>

            {/* Scope */}
            <div style={{ paddingRight: 16, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
                {test.scopeCount} units
              </div>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.60)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {test.scopeUnits.join(', ')}
              </div>
            </div>

            {/* Testers */}
            <div style={{ paddingRight: 16 }}>
              <AvatarStack testers={test.testers} />
            </div>

            {/* Feedback */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--lumx-color-green-N)', fontSize: 13 }}>
                <MdiIcon path={mdiThumbUpOutline} size={0.75} color="var(--lumx-color-green-N)" />
                {test.thumbsUp}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--lumx-color-red-N)', fontSize: 13 }}>
                <MdiIcon path={mdiThumbDownOutline} size={0.75} color="var(--lumx-color-red-N)" />
                {test.thumbsDown}
              </span>
            </div>

            {/* Start date */}
            <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.87)', paddingRight: 16 }}>
              {test.startDate}
            </span>

            {/* Status */}
            <StatusFlag test={test} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestDeploymentPage;
